import React, { useState, useEffect } from 'react';
import { Property, Tenant, Invoice, MaintenanceRequest, Transaction } from '../types';
import { Wallet, Users, Wrench, AlertCircle, TrendingUp, Building2, CheckCircle, Clock } from 'lucide-react';
import { api } from '../services/api';

interface DashboardProps {
  properties: Property[];
  tenants: Tenant[];
  invoices: Invoice[];
  maintenance: MaintenanceRequest[];
  transactions: Transaction[];
  onNavigate?: (tab: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ properties, tenants, invoices, maintenance, transactions, onNavigate }) => {
  const [treasuryBalance, setTreasuryBalance] = useState<number | null>(null);

  useEffect(() => {
    api.accounting.getTreasuryBalance()
      .then(data => setTreasuryBalance(data.balance))
      .catch(err => console.error("Error fetching treasury balance:", err));
  }, []);

  // Calculations
  const totalCollected = invoices.filter(i => i.status === 'Paid').reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
  const totalExpected = invoices.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
  const totalUncollected = invoices.filter(i => i.status === 'Pending' || i.status === 'Late').reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
  
  const additionalIncomes = transactions.filter(t => t.type === 'Income' && !t.invoiceId).reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
  const totalExpenses = transactions.filter(t => t.type === 'Expense').reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
  
  const netIncome = (totalCollected + additionalIncomes) - totalExpenses;
  
  const totalUnits = properties.reduce((acc, p) => acc + p.units.length, 0);
  const occupiedUnits = properties.reduce((acc, p) => acc + p.units.filter(u => u.tenantId).length, 0);
  const occupancyRate = totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0;
  
  const pendingMaintenance = maintenance.filter(m => m.status !== 'Completed').length;
  const lateInvoicesCount = invoices.filter(i => i.status === 'Late').length;
  const collectionRate = totalExpected > 0 ? Math.round((totalCollected / totalExpected) * 100) : 0;
  const maxChartVal = Math.max(totalExpected, totalCollected, totalExpenses);
  const expectedWidth = maxChartVal > 0 ? Math.round((totalExpected / maxChartVal) * 100) : 0;
  const collectedWidth = maxChartVal > 0 ? Math.round((totalCollected / maxChartVal) * 100) : 0;
  const expensesWidth = maxChartVal > 0 ? Math.round((totalExpenses / maxChartVal) * 100) : 0;

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-slate-900 to-fly-800 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
             <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
             </svg>
        </div>
        <div className="relative z-10 flex flex-col lg:flex-row justify-between items-center gap-6">
            <div className="text-center lg:text-right">
                <h2 className="text-2xl md:text-3xl font-bold mb-2">لوحة التحكم الرئيسية</h2>
                <p className="text-fly-100 text-base md:text-lg opacity-90">نظرة شاملة على أداء محفظتك العقارية اليوم.</p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
                <div className="text-center bg-white/10 p-3 md:p-4 rounded-2xl backdrop-blur-sm min-w-[120px]">
                    <div className="text-xs text-fly-200">صافي الدخل</div>
                    <div className="text-xl md:text-2xl font-bold">{netIncome.toLocaleString()} KWD</div>
                </div>
                <div className="text-center bg-white/10 p-3 md:p-4 rounded-2xl backdrop-blur-sm min-w-[120px]">
                    <div className="text-xs text-fly-200">نسبة الإشغال</div>
                    <div className="text-xl md:text-2xl font-bold">{occupancyRate}%</div>
                </div>
            </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Card 1: الرصيد النقدي */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-between group hover:shadow-md transition-all min-h-[110px]">
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <p className="text-slate-500 text-xs font-medium mb-1 truncate">الرصيد النقدي الفعلي</p>
                    <h3 className={`text-lg font-bold leading-tight break-all ${treasuryBalance !== null && treasuryBalance < 0 ? 'text-rose-600' : 'text-slate-800'}`}>
                        <span>{treasuryBalance !== null ? Number(treasuryBalance).toLocaleString() : '...'}</span>
                        <span className="text-[10px] text-slate-400 font-normal ml-1"> KWD</span>
                    </h3>
                </div>
                <div className="bg-emerald-50 p-2 rounded-xl group-hover:bg-emerald-100 transition-colors shrink-0">
                    <Wallet className="w-5 h-5 text-emerald-600" />
                </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">النقدية بالصندوق والبنك</p>
        </div>

        {/* Card 2: مستحقات معلقة */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-between group hover:shadow-md transition-all min-h-[110px]">
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <p className="text-slate-500 text-xs font-medium mb-1 truncate">مستحقات معلقة</p>
                    <h3 className="text-lg font-bold text-slate-800 leading-tight break-all">
                        <span>{totalUncollected.toLocaleString()}</span>
                        <span className="text-[10px] text-slate-400 font-normal ml-1"> KWD</span>
                    </h3>
                </div>
                <div className="bg-orange-50 p-2 rounded-xl group-hover:bg-orange-100 transition-colors shrink-0">
                    <Clock className="w-5 h-5 text-orange-600" />
                </div>
            </div>
            <p className="text-[10px] text-orange-500 mt-1">فواتير لم تُحصل بعد</p>
        </div>

        {/* Card 3: إجمالي المحصل */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-between group hover:shadow-md transition-all min-h-[110px]">
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <p className="text-slate-500 text-xs font-medium mb-1 truncate">إجمالي المحصل</p>
                    <h3 className="text-lg font-bold text-slate-800 leading-tight break-all">
                        <span>{totalCollected.toLocaleString()}</span>
                        <span className="text-[10px] text-slate-400 font-normal ml-1"> KWD</span>
                    </h3>
                </div>
                <div className="bg-green-50 p-2 rounded-xl group-hover:bg-green-100 transition-colors shrink-0">
                    <Wallet className="w-5 h-5 text-green-600" />
                </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">من أصل {totalExpected.toLocaleString()} متوقع ({collectionRate}%)</p>
        </div>

        {/* Card 4: المستأجرين */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-between group hover:shadow-md transition-all min-h-[110px]">
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <p className="text-slate-500 text-xs font-medium mb-1 truncate">المستأجرين النشطين</p>
                    <h3 className="text-2xl font-bold text-slate-800 leading-tight">{tenants.length}</h3>
                </div>
                <div className="bg-blue-50 p-2 rounded-xl group-hover:bg-blue-100 transition-colors shrink-0">
                    <Users className="w-5 h-5 text-blue-600" />
                </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">مستأجر نشط</p>
        </div>

        {/* Card 5: الصيانة */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-between group hover:shadow-md transition-all min-h-[110px]">
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <p className="text-slate-500 text-xs font-medium mb-1 truncate">طلبات الصيانة</p>
                    <h3 className="text-2xl font-bold text-slate-800 leading-tight">{pendingMaintenance}</h3>
                </div>
                <div className="bg-orange-50 p-2 rounded-xl group-hover:bg-orange-100 transition-colors shrink-0">
                    <Wrench className="w-5 h-5 text-orange-600" />
                </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">قيد الانتظار</p>
        </div>

        {/* Card 6: الفواتير المتأخرة */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-between group hover:shadow-md transition-all min-h-[110px]">
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <p className="text-slate-500 text-xs font-medium mb-1 truncate">فواتير متأخرة</p>
                    <h3 className="text-2xl font-bold text-slate-800 leading-tight">{lateInvoicesCount}</h3>
                </div>
                <div className="bg-red-50 p-2 rounded-xl group-hover:bg-red-100 transition-colors shrink-0">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
            </div>
            <p className="text-[10px] text-red-400 mt-1">تتطلب المتابعة الفورية</p>
        </div>
      </div>

      {/* Charts & Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Chart Area (Simulated) */}
        <div className="lg:col-span-2 bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-fly-600" />
                    تحليل الإيرادات والمصروفات
                </h3>
                <select className="bg-slate-50 border-none text-sm rounded-lg p-2 outline-none text-slate-600">
                    <option>أكتوبر 2023</option>
                    <option>سبتمبر 2023</option>
                </select>
            </div>
            
            {/* CSS Bar Chart Simulation */}
            <div className="space-y-6">
                <div className="space-y-2">
                    <div className="flex justify-between text-sm text-slate-600">
                        <span>الإيرادات المتوقعة</span>
                        <span className="font-bold">{totalExpected.toLocaleString()} KWD</span>
                    </div>
                    <div className="w-full bg-slate-100 h-3 rounded-full overflow-hidden">
                        <div className="bg-fly-500 h-full rounded-full transition-all duration-500" style={{ width: `${expectedWidth}%` }}></div>
                    </div>
                </div>
                
                <div className="space-y-2">
                    <div className="flex justify-between text-sm text-slate-600">
                        <span>التحصيل الفعلي</span>
                        <span className="font-bold">{totalCollected.toLocaleString()} KWD</span>
                    </div>
                    <div className="w-full bg-slate-100 h-3 rounded-full overflow-hidden">
                        <div className="bg-green-500 h-full rounded-full transition-all duration-500" style={{ width: `${collectedWidth}%` }}></div>
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="flex justify-between text-sm text-slate-600">
                        <span>المصروفات</span>
                        <span className="font-bold">{totalExpenses.toLocaleString()} KWD</span>
                    </div>
                    <div className="w-full bg-slate-100 h-3 rounded-full overflow-hidden">
                        <div className="bg-red-400 h-full rounded-full transition-all duration-500" style={{ width: `${expensesWidth}%` }}></div>
                    </div>
                </div>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                 <div>
                    <div className="text-xs text-slate-400 mb-1">أعلى عقار دخلاً</div>
                    <div className="font-bold text-slate-700 text-sm">برج الجوهرة</div>
                 </div>
                 <div>
                    <div className="text-xs text-slate-400 mb-1">متوسط الإيجار</div>
                    <div className="font-bold text-slate-700 text-sm">650 KWD</div>
                 </div>
                 <div>
                    <div className="text-xs text-slate-400 mb-1">الوحدات الشاغرة</div>
                    <div className="font-bold text-slate-700 text-sm">{totalUnits - occupiedUnits}</div>
                 </div>
            </div>
        </div>

        {/* Side List: Recent Maintenance */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
             <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-500" />
                آخر نشاطات الصيانة
             </h3>
             <div className="space-y-4">
                {maintenance.slice(0, 4).map((req) => (
                    <div key={req.id} className="flex gap-4 items-start p-3 hover:bg-slate-50 rounded-xl transition-colors border border-transparent hover:border-slate-100">
                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                            req.status === 'Completed' ? 'bg-green-500' :
                            req.status === 'In Progress' ? 'bg-blue-500' : 'bg-orange-500'
                        }`} />
                        <div>
                            <h4 className="text-sm font-bold text-slate-800 line-clamp-1">{req.description}</h4>
                            <p className="text-xs text-slate-500 mt-0.5">{req.propertyName} - {req.unitNumber || 'عام'}</p>
                            <span className="text-[10px] text-slate-400 mt-1 block">{req.dateReported}</span>
                        </div>
                    </div>
                ))}
                {maintenance.length === 0 && <p className="text-sm text-slate-400 text-center py-4">لا توجد طلبات صيانة حديثة</p>}
             </div>
             <button 
                onClick={() => onNavigate?.('maintenance')}
                className="w-full mt-4 py-2 text-sm text-fly-600 font-bold hover:bg-fly-50 rounded-lg transition-colors"
             >
                عرض كل الطلبات
             </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;